import { useEffect, useState, useMemo } from 'react'
import { Formula, Vehicle } from '@otw/models'
import { useFormulaRules } from 'hooks/graybook/useFormulaRules'
import { calculatePrice } from '@/hooks/graybook/useFormula'
import { useFormulas } from './useFormulas'
import { useAuth } from 'hooks/auth'
import { matchFormula } from './matchFormula'
import { useOLSPrediction, useOLSVersions} from '@/hooks/ols'

interface CalculateDealInput {
  make: string
  model: string
  type: string
  size: string
  miles: string
  year: string
  sellerAskingPrice: string
  fuel: Vehicle['fuel']
}

type DealQualityInfo = {
  label: string
  color: 'success' | 'primary' | 'warning' | 'error' | 'info'
  displayText: string
}

interface CalculateDealOutput {
  grayBookWholeSalePrice: number | null
  grayBookRetailPrice: number | null
  dealQuality: Vehicle['dealQuality']
  dealQualityInfo: DealQualityInfo | null
  matchedFormula: Formula | null
}

// BUSINESS RULE: Fixed incentive amount for non-owners
export const INCENTIVE_AMOUNT = 500

// Function to get incentive adjustment based on owner status
export const getIncentiveAdjustment = (isOwner: boolean): number => {
  return isOwner ? 0 : INCENTIVE_AMOUNT
}

// Function to adjust price based on owner status
export const adjustPriceForIncentive = (
  price: number,
  isOwner: boolean,
  isReverseAdjustment: boolean = false,
): number => {
  const adjustment = getIncentiveAdjustment(isOwner)
  // When reversing the adjustment (e.g., for display purposes), we add instead of subtract
  return isReverseAdjustment ? price + adjustment : price - adjustment
}

export const getDealQuality = (
  askingPrice: number,
  wholesalePrice: number,
  isOwner: boolean,
): Vehicle['dealQuality'] => {
  // BUSINESS RULE:
  // For non-owner users, we artificially increase the asking price by $500
  // This makes deals appear worse than they actually are for regular users,
  // incentivizing users to not sell their vehicles exactly on price point
  const adjustedAskingPrice = askingPrice + getIncentiveAdjustment(isOwner)

  const priceDiff = ((adjustedAskingPrice - wholesalePrice) / wholesalePrice) * 100

  if (priceDiff <= -10) return 'great'
  if (priceDiff <= 0) return 'good'
  if (priceDiff <= 10) return 'average'
  if (priceDiff <= 20) return 'belowAverage'
  return 'poor'
}

export const getPriceForQuality = (
  targetQuality: Vehicle['dealQuality'],
  wholesalePrice: number,
  isOwner: boolean = false,
): number | null => {
  // BUSINESS RULE:
  // For non-owner users, we need to account for the $500 incentive adjustment
  // This means we need to adjust our target prices to compensate for this artificial increase
  const incentiveAdjustment = isOwner ? 0 : 500

  // Calculate base price first
  let basePrice: number
  switch (targetQuality) {
    case 'great':
      basePrice = Math.round(wholesalePrice * 0.9) // 10% below wholesale for great deals
      break
    case 'good':
      basePrice = Math.round(wholesalePrice) // At wholesale price for good deals
      break
    case 'average':
      basePrice = Math.round(wholesalePrice * 1.1) // 10% above wholesale for average deals
      break
    case 'belowAverage':
      basePrice = Math.round(wholesalePrice * 1.2) // 20% above wholesale for below average deals
      break
    default:
      return null
  }

  // Adjust the price to account for the incentive if not an owner
  return basePrice - incentiveAdjustment
}

export const getDealQualityInfo = (quality: Vehicle['dealQuality']): DealQualityInfo => {
  if (!quality) {
    return { label: 'Unkown', color: 'error', displayText: 'Unknown' }
  }
  const displayText = quality.charAt(0).toUpperCase() + quality.slice(1)
  switch (quality) {
    case 'great':
      return { label: 'Great', color: 'success', displayText }
    case 'good':
      return { label: 'Good', color: 'info', displayText }
    case 'average':
      return { label: 'Average', color: 'primary', displayText }
    case 'belowAverage':
      return { label: 'Below Average', color: 'warning', displayText }
    case 'poor':
      return { label: 'Poor', color: 'error', displayText }
    default:
      return { label: 'Unkown', color: 'error', displayText }
  }
}

export const useCalculateDeal = (input: CalculateDealInput): CalculateDealOutput => {
  const [matchedFormula, setMatchedFormula] = useState<Formula | null>(null)
  const [grayBookRetailPrice, setGrayBookRetailPrice] = useState<number | null>(null)
  const [grayBookWholeSalePrice, setGrayBookWholeSalePrice] = useState<number | null>(null)
  const [dealQuality, setDealQuality] = useState<Vehicle['dealQuality'] | null>(null)
  const [dealQualityInfo, setDealQualityInfo] = useState<DealQualityInfo | null>(null)
  const { predictPrice } = useOLSPrediction()

  const { formulas } = useFormulas({})
  const { rules } = useFormulaRules({ formulaId: matchedFormula?.id })

  // Get current user's role to apply price adjustments
  const { userInfo } = useAuth()
  const isOwner = userInfo?.isOwner || false

  // Construct category ID for OLS lookup
  const categoryId = useMemo(() => {
    if (!matchedFormula) return null
    const parts = [
      matchedFormula.vehicleMake.toLowerCase().replace(/\s+/g, '-'),
      matchedFormula.vehicleModel.toLowerCase().replace(/\s+/g, '-'),
      matchedFormula.vehicleType.toLowerCase().replace(/\s+/g, '-'),
      matchedFormula.vehicleSize.toLowerCase().replace(/\s+/g, '-'),
    ]
    return parts.join('_')
  }, [matchedFormula])

  // Fetch OLS version if automated pricing is enabled
  const { activeVersion } = useOLSVersions({
    categoryId: categoryId,
    enabled: !!categoryId && !!matchedFormula?.useAutomatedPricing,
  })

  console.log('active version', activeVersion)

  // Match formula
  useEffect(() => {
    const { make, model, type, size, fuel } = input
    if (formulas) {
      const matched = matchFormula(formulas, { make, model, type, size, fuel })
      setMatchedFormula(matched)
      if (!matched) {
        setGrayBookRetailPrice(null)
        setGrayBookWholeSalePrice(null)
        setDealQuality(null)
      }
    } else {
      setMatchedFormula(null)
      setGrayBookRetailPrice(null)
      setGrayBookWholeSalePrice(null)
      setDealQuality(null)
    }
  }, [input, formulas])

  // Calculate price - either using OLS or Formula
  useEffect(() => {
    let isMounted = true

    const calculateDealPrice = async () => {
      const { miles, year, fuel } = input
      if (!matchedFormula || !miles || !year) {
        return
      }

      const yearNum = typeof year === 'string' ? Number(year) : year
      const milesNum = typeof miles === 'string' ? Number(miles.replace(/,/g, '')) : miles

      // Check if we should use OLS automated pricing
      if (matchedFormula.useAutomatedPricing && activeVersion) {
        if (!fuel) {
          return
        }

        // Use OLS prediction
        const { buyInPrice, sellPrice } = await predictPrice({
          year: yearNum,
          mileage: milesNum,
          make: input.make,
          model: input.model,
          type: input.type,
          size: input.size,
          color: 'White',
          fuel: fuel,
        })

        if (!isMounted) return
        setGrayBookRetailPrice(sellPrice)
        setGrayBookWholeSalePrice(buyInPrice)
      } else if (rules) {
        // Fall back to formula calculation
        const result = calculatePrice({
          input: {
            year: yearNum,
            mileage: milesNum,
          },
          formula: matchedFormula,
          rules,
        })
        if (!isMounted) return
        setGrayBookRetailPrice(result.price)
        const wholeSalePrice = result.price * (1 - (matchedFormula.wholesaleDiscount || 0) / 100)
        setGrayBookWholeSalePrice(wholeSalePrice)
      }
    }

    void calculateDealPrice()

    return () => {
      isMounted = false
    }
  }, [matchedFormula, rules, input, activeVersion, predictPrice])

  useEffect(() => {
    if (grayBookWholeSalePrice && input.sellerAskingPrice) {
      const askingPrice = Number(input.sellerAskingPrice)
      const quality = getDealQuality(askingPrice, grayBookWholeSalePrice, isOwner)
      setDealQuality(quality)
      setDealQualityInfo(getDealQualityInfo(quality))
    } else {
      setDealQuality(null)
      setDealQualityInfo(null)
    }
  }, [grayBookRetailPrice, input.sellerAskingPrice, grayBookWholeSalePrice, isOwner])

  return { grayBookRetailPrice, grayBookWholeSalePrice, dealQuality, dealQualityInfo, matchedFormula }
}
